'use client'

import Link from 'next/link'
import {
  type ComponentProps,
  type FocusEvent,
  type PointerEvent,
  useCallback,
  useState,
} from 'react'
import { isEmailsGroupCanvasPath } from '@/lib/emails/emails-route'
import { useRouteCodePreload } from './route-code-preload-context'

/**
 * A `<Link>` for list rows that open a detail page (`/x/[id]`). By default it
 * prefetches the destination's App Shell like any link; once the person
 * shows intent (pointer over it, or keyboard focus) it upgrades to a
 * per-link prefetch, which resolves the row's `params` and the page's
 * prefetchable seeds — so the click paints the record, not its skeleton.
 *
 * Per-link prefetching on every visible row would cost one server render per
 * row; on intent it costs one per row the person reaches for (Next.js
 * "Optimizing prefetching": prefetch on intent for lists and grids).
 *
 * `isEager` starts the per-link prefetch as soon as the link is visible — for
 * the few most-likely rows (the top of a feed), whose detail prefetch takes
 * longer than a quick hover-and-click.
 */
export function IntentPrefetchLink({
  href,
  isEager = false,
  isCanvasFocusNavigation = false,
  onPointerEnter,
  onFocus,
  ...props
}: Omit<ComponentProps<typeof Link>, 'prefetch'> & {
  isEager?: boolean
  /** The caller owns focus changes through the persistent canvas controller. */
  isCanvasFocusNavigation?: boolean
}) {
  const [intentPrefetch, setIntentPrefetch] = useState<boolean | null>(null)
  const preloadRouteCode = useRouteCodePreload()
  const warmDestination = useCallback(() => {
    const pathname = typeof href === 'string' ? href : href.pathname
    if (!pathname) {
      return
    }
    const targetPath = pathname.split(/[?#]/)[0]
    // Read the live URL only on intent. Subscribing every transcript link to
    // pathname changes would re-render the thread during page navigation.
    if (
      isCanvasFocusNavigation &&
      targetPath === window.location.pathname &&
      isEmailsGroupCanvasPath(targetPath)
    ) {
      setIntentPrefetch(false)
      return
    }
    setIntentPrefetch(true)
    preloadRouteCode?.(pathname)
  }, [href, isCanvasFocusNavigation, preloadRouteCode])
  const handlePointerEnter = useCallback(
    (event: PointerEvent<HTMLAnchorElement>) => {
      warmDestination()
      onPointerEnter?.(event)
    },
    [onPointerEnter, warmDestination]
  )
  const handleFocus = useCallback(
    (event: FocusEvent<HTMLAnchorElement>) => {
      warmDestination()
      onFocus?.(event)
    },
    [onFocus, warmDestination]
  )
  const prefetch = isEager && intentPrefetch !== false ? true : intentPrefetch
  return (
    <Link
      {...props}
      href={href}
      prefetch={prefetch}
      onPointerEnter={handlePointerEnter}
      onFocus={handleFocus}
    />
  )
}
